import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Tag,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Statistic,
  Progress,
  Typography,
  Alert,
  Popconfirm,
  Tooltip,
  Timeline,
  Descriptions,
  Switch
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  SettingOutlined,
  CloudUploadOutlined,
  FileZipOutlined,
  CodeOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface VersionInfo {
  version: string;
  timestamp: string;
  build: number;
}

interface VersionHistory {
  version: string;
  timestamp: string;
  description: string;
  changedFiles: number;
  totalFiles: number;
}

interface ChangedFile {
  path: string;
  size: number;
  modified: string;
}

interface PackageInfo {
  name: string;
  path: string;
  size: number;
  sizeMB: string;
  created: string;
  modified: string;
  type: 'incremental' | 'full';
}

interface ServerConfig {
  host: string;
  port: number;
  username: string;
  deployPath: string;
  password?: string;
}

const VersionManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const [changedFiles, setChangedFiles] = useState<ChangedFile[]>([]);
  const [packages, setPackages] = useState<PackageInfo[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [servers, setServers] = useState<Record<string, ServerConfig>>({});
  const [defaultServer, setDefaultServer] = useState<string>('');
  
  // 模态框状态
  const [bumpModalVisible, setBumpModalVisible] = useState(false);
  const [packageModalVisible, setPackageModalVisible] = useState(false);
  const [serverModalVisible, setServerModalVisible] = useState(false);
  const [deployModalVisible, setDeployModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [editingServer, setEditingServer] = useState<any>(null);
  const [autoDeployModalVisible, setAutoDeployModalVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [deploying, setDeploying] = useState(false);
  const [deployLogs, setDeployLogs] = useState<any[]>([]);
  const [deployLogsModalVisible, setDeployLogsModalVisible] = useState(false);
  const [refreshingLogs, setRefreshingLogs] = useState(false);
  const [changedFilesModalVisible, setChangedFilesModalVisible] = useState(false);
  
  // 表单状态
  const [bumpForm] = Form.useForm();
  const [packageForm] = Form.useForm();
  const [serverForm] = Form.useForm();
  const [deployForm] = Form.useForm();

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const [versionRes, historyRes, changesRes, packagesRes, serversRes] = await Promise.all([
        api.get('/version-management/version'),
        api.get('/version-management/history'),
        api.get('/version-management/changes'),
        api.get('/version-management/packages'),
        api.get('/version-management/servers')
      ]);

      setVersionInfo(versionRes.data.data);
      setVersionHistory(historyRes.data.data);
      setChangedFiles(changesRes.data.data.files);
      setPackages(packagesRes.data.data);
      setServers(serversRes.data.data.servers);
      setDefaultServer(serversRes.data.data.defaultServer);
    } catch (error: any) {
      message.error('加载数据失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 刷新变更文件
  const refreshChangedFiles = async () => {
    try {
      const changesRes = await api.get('/version-management/changes');
      setChangedFiles(changesRes.data.data.files);
      message.success('变更文件已刷新');
    } catch (error: any) {
      message.error('刷新失败: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    loadData();
    loadDeployLogs();
  }, []);

  // 更新版本号
  const handleBumpVersion = async (values: any) => {
    try {
      setLoading(true);
      const response = await api.post('/version-management/bump', {
        type: values.type,
        description: values.description || ''
      });
      
      message.success(response.data.message);
      setBumpModalVisible(false);
      bumpForm.resetFields();
      loadData();
    } catch (error: any) {
      message.error('更新版本失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 创建更新包
  const handleCreatePackage = async (values: any) => {
    try {
      setLoading(true);
      const endpoint = values.type === 'incremental' 
        ? '/version-management/incremental' 
        : '/version-management/full';
      
      const response = await api.post(endpoint, {
        description: values.description,
        build: values.build || false
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        setPackageModalVisible(false);
        packageForm.resetFields();
        loadData();
      } else {
        message.warning(response.data.message);
      }
    } catch (error: any) {
      message.error('创建更新包失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 添加服务器
  const handleAddServer = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingServer) {
        // 编辑模式
        await api.put(`/version-management/servers/${editingServer.id}`, values);
        message.success('服务器配置更新成功');
      } else {
        // 添加模式
        await api.post('/version-management/servers', values);
        message.success('服务器配置添加成功');
      }
      
      setServerModalVisible(false);
      serverForm.resetFields();
      setEditingServer(null);
      loadData();
    } catch (error: any) {
      message.error((editingServer ? '更新' : '添加') + '服务器失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 设置默认服务器
  const handleSetDefaultServer = async (serverName: string) => {
    try {
      await api.post('/version-management/servers/default', {
        name: serverName
      });
      
      message.success(`默认服务器已设置为: ${serverName}`);
      loadData();
    } catch (error: any) {
      message.error('设置默认服务器失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 编辑服务器
  const handleEditServer = (server: any) => {
    setEditingServer(server);
    serverForm.setFieldsValue({
      name: server.name,
      host: server.host,
      port: server.port,
      username: server.username,
      password: server.password,
      deployPath: server.deploy_path,
      backupPath: server.backup_path
    });
    setServerModalVisible(true);
  };

  // 删除服务器
  const handleDeleteServer = async (serverId: number) => {
    try {
      await api.delete(`/version-management/servers/${serverId}`);
      message.success('服务器配置删除成功');
      loadData();
    } catch (error: any) {
      message.error('删除服务器失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 自动部署
  const handleAutoDeploy = (packageInfo: any) => {
    setSelectedPackage(packageInfo);
    setAutoDeployModalVisible(true);
  };

  // 执行自动部署
  const handleExecuteAutoDeploy = async (values: any) => {
    try {
      setDeploying(true);
      
      const response = await api.post('/version-management/deploy/auto', {
        packagePath: selectedPackage.path,
        serverId: values.serverId,
        type: selectedPackage.type
      });
      
      if (response.data.success) {
        message.success('自动部署已开始！');
        setAutoDeployModalVisible(false);
        setSelectedPackage(null);
        // 刷新部署日志
        loadDeployLogs();
      } else {
        message.error('自动部署失败: ' + response.data.message);
      }
    } catch (error: any) {
      message.error('自动部署失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeploying(false);
    }
  };

  // 加载部署日志
  const loadDeployLogs = async (showLoading = false) => {
    try {
      if (showLoading) {
        setRefreshingLogs(true);
      }
      const response = await api.get('/version-management/deploy/logs?limit=50');
      if (response.data.success) {
        setDeployLogs(response.data.data.logs);
        if (showLoading) {
          message.success('部署日志已刷新');
        }
      }
    } catch (error: any) {
      console.error('加载部署日志失败:', error.message);
      if (showLoading) {
        message.error('刷新部署日志失败: ' + error.message);
      }
    } finally {
      if (showLoading) {
        setRefreshingLogs(false);
      }
    }
  };

  // 检查服务器版本
  const handleCheckServerVersion = async (server: any) => {
    try {
      const response = await api.post('/version-management/servers/check-version', {
        serverId: server.id
      });
      
      if (response.data.success) {
        message.success(`服务器 ${server.name} 当前版本: ${response.data.data.version}`);
        // 刷新服务器列表
        loadData();
      } else {
        message.error('检查版本失败: ' + response.data.message);
      }
    } catch (error: any) {
      message.error('检查版本失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 生成部署脚本
  const handleGenerateDeployScript = async (values: any) => {
    try {
      setLoading(true);
      const response = await api.post('/version-management/deploy/script', {
        packagePath: values.packagePath,
        serverName: values.serverName,
        type: values.type
      });
      
      if (response.data.success) {
        message.success('部署脚本生成成功');
        setDeployModalVisible(false);
        deployForm.resetFields();
        
        // 显示部署命令
        Modal.info({
          title: '部署命令',
          width: 800,
          content: (
            <div>
              <Alert
                message="部署步骤"
                description="请按顺序执行以下命令来完成部署"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <div style={{ marginBottom: 16 }}>
                <Text strong>1. 上传文件到服务器:</Text>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  marginTop: '8px'
                }}>
                  {response.data.data.commands.upload}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>2. 执行部署脚本:</Text>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  marginTop: '8px'
                }}>
                  {response.data.data.commands.execute}
                </div>
              </div>
              <div>
                <Text strong>3. 检查服务状态:</Text>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  marginTop: '8px'
                }}>
                  {response.data.data.commands.check}
                </div>
              </div>
            </div>
          )
        });
      }
    } catch (error: any) {
      message.error('生成部署脚本失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 下载更新包
  const handleDownloadPackage = (filename: string) => {
    const link = document.createElement('a');
    link.href = `/api/version-management/packages/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 删除更新包
  const handleDeletePackage = async (filename: string) => {
    try {
      await api.delete(`/version-management/packages/${filename}`);
      message.success('文件删除成功');
      loadData();
    } catch (error: any) {
      message.error('删除文件失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 批量删除更新包
  const handleBatchDeletePackages = async () => {
    if (selectedPackages.length === 0) {
      message.warning('请选择要删除的包');
      return;
    }

    try {
      setLoading(true);
      const deletePromises = selectedPackages.map(packageName => 
        api.delete(`/version-management/packages/${packageName}`)
      );
      
      await Promise.all(deletePromises);
      message.success(`成功删除 ${selectedPackages.length} 个包`);
      setSelectedPackages([]);
      loadData();
    } catch (error: any) {
      message.error('批量删除失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 表格列定义
  const packageColumns = [
    {
      title: '文件名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: PackageInfo) => (
        <Space>
          <FileZipOutlined />
          <Text strong>{text}</Text>
          <Tag color={record.type === 'incremental' ? 'blue' : 'green'}>
            {record.type === 'incremental' ? '增量' : '完整'}
          </Tag>
        </Space>
      )
    },
    {
      title: '大小',
      dataIndex: 'sizeMB',
      key: 'sizeMB',
      render: (text: string) => `${text} MB`
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: '操作',
      key: 'actions',
      render: (text: any, record: PackageInfo) => (
        <Space>
          <Tooltip title="下载">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadPackage(record.name)}
            />
          </Tooltip>
          <Tooltip title="自动部署">
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              size="small"
              onClick={() => handleAutoDeploy(record)}
            />
          </Tooltip>
          <Tooltip title="生成部署脚本">
            <Button
              icon={<PlayCircleOutlined />}
              size="small"
              onClick={() => {
                deployForm.setFieldsValue({
                  packagePath: record.path,
                  type: record.type
                });
                setDeployModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个文件吗？"
            onConfirm={() => handleDeletePackage(record.name)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const serverColumns = [
    {
      title: '服务器名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <Space>
          <CloudUploadOutlined />
          <Text strong>{text}</Text>
          {record.is_default && <Tag color="green">默认</Tag>}
        </Space>
      )
    },
    {
      title: '主机地址',
      dataIndex: 'host',
      key: 'host',
      render: (text: string, record: ServerConfig) => `${text}:${record.port}`
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: '部署路径',
      dataIndex: 'deploy_path',
      key: 'deploy_path',
      render: (text: string) => (
        <Text code style={{ fontSize: '12px' }}>{text}</Text>
      )
    },
    {
      title: '当前版本',
      dataIndex: 'current_version',
      key: 'current_version',
      render: (text: string, record: any) => (
        <Space>
          <Tag color="blue">{text || '未知'}</Tag>
          <Button 
            size="small" 
            type="link"
            onClick={() => handleCheckServerVersion(record)}
          >
            检查
          </Button>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'actions',
      render: (text: any, record: any, index: number) => (
        <Space>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleEditServer(record)}
          >
            编辑
          </Button>
          {!record.is_default && (
            <Button
              size="small"
              onClick={() => handleSetDefaultServer(record.name)}
            >
              设为默认
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个服务器配置吗？"
            onConfirm={() => handleDeleteServer(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.is_default}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.is_default}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <CodeOutlined /> 版本管理系统
        </Title>
        <Button
          icon={<HistoryOutlined />}
          onClick={() => setDeployLogsModalVisible(true)}
        >
          部署日志
        </Button>
      </div>
      
      <Row gutter={[16, 16]}>
        {/* 版本信息卡片 */}
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="当前版本"
              value={versionInfo?.version || '1.0.0'}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="版本历史"
              value={versionHistory.length}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card
            actions={[
              <Button 
                key="refresh" 
                type="link" 
                icon={<ReloadOutlined />}
                onClick={refreshChangedFiles}
              >
                刷新
              </Button>,
              ...(changedFiles.length > 0 ? [
                <Button 
                  key="view" 
                  type="link" 
                  icon={<EyeOutlined />}
                  onClick={() => setChangedFilesModalVisible(true)}
                >
                  查看详情
                </Button>
              ] : [])
            ]}
          >
            <Statistic
              title="变更文件"
              value={changedFiles.length}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: changedFiles.length > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="更新包"
              value={packages.length}
              prefix={<FileZipOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* 版本控制 */}
        <Col xs={24} lg={12}>
          <Card
            title="版本控制"
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setBumpModalVisible(true)}
                >
                  更新版本
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => setHistoryModalVisible(true)}
                >
                  版本历史
                </Button>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                message="版本信息"
                description={
                  <div>
                    <p>当前版本: <Text strong>{versionInfo?.version}</Text></p>
                    <p>构建时间: <Text>{versionInfo?.timestamp ? new Date(versionInfo.timestamp).toLocaleString() : '未知'}</Text></p>
                    <p>构建号: <Text>{versionInfo?.build}</Text></p>
                  </div>
                }
                type="info"
                showIcon
              />
              
              {changedFiles.length > 0 && (
                <Alert
                  message="检测到文件变更"
                  description={`发现 ${changedFiles.length} 个文件发生变更，可以创建增量更新包`}
                  type="warning"
                  showIcon
                  action={
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => {
                        packageForm.setFieldsValue({ type: 'incremental', build: false });
                        setPackageModalVisible(true);
                      }}
                    >
                      创建增量包
                    </Button>
                  }
                />
              )}
            </Space>
          </Card>
        </Col>

        {/* 服务器管理 */}
        <Col xs={24} lg={12}>
          <Card
            title="服务器管理"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingServer(null);
                  serverForm.resetFields();
                  setServerModalVisible(true);
                }}
              >
                添加服务器
              </Button>
            }
          >
            <Table
              dataSource={Object.values(servers)}
              columns={serverColumns}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>
      </Row>

      {/* 更新包管理 */}
      <Card
        title="更新包管理"
        extra={
          <Space>
            {selectedPackages.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedPackages.length} 个包吗？`}
                onConfirm={handleBatchDeletePackages}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={loading}
                >
                  批量删除 ({selectedPackages.length})
                </Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => {
                packageForm.setFieldsValue({ type: 'incremental', build: false });
                setPackageModalVisible(true);
              }}
            >
              创建增量包
            </Button>
            <Button
              icon={<FileZipOutlined />}
              onClick={() => {
                packageForm.setFieldsValue({ type: 'full', build: false });
                setPackageModalVisible(true);
              }}
            >
              创建完整包
            </Button>
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        <Table
          dataSource={packages}
          columns={packageColumns}
          pagination={{ pageSize: 10 }}
          loading={loading}
          rowKey="name"
          rowSelection={{
            selectedRowKeys: selectedPackages,
            onChange: (selectedRowKeys: React.Key[]) => {
              setSelectedPackages(selectedRowKeys as string[]);
            },
            getCheckboxProps: (record: PackageInfo) => ({
              name: record.name,
            }),
          }}
        />
      </Card>

      {/* 更新版本模态框 */}
      <Modal
        title="更新版本号"
        open={bumpModalVisible}
        onCancel={() => setBumpModalVisible(false)}
        footer={null}
      >
        <Form
          form={bumpForm}
          layout="vertical"
          onFinish={handleBumpVersion}
        >
          <Form.Item
            name="type"
            label="版本类型"
            rules={[{ required: true, message: '请选择版本类型' }]}
          >
            <Select>
              <Option value="patch">补丁版本 (1.0.0 → 1.0.1)</Option>
              <Option value="minor">次版本 (1.0.0 → 1.1.0)</Option>
              <Option value="major">主版本 (1.0.0 → 2.0.0)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="更新描述"
            tooltip="可选，用于记录本次版本更新的主要内容"
          >
            <TextArea
              rows={3}
              placeholder="例如：修复文件变更检测逻辑，优化备份功能..."
              maxLength={200}
              showCount
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                更新版本
              </Button>
              <Button onClick={() => setBumpModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建更新包模态框 */}
      <Modal
        title="创建更新包"
        open={packageModalVisible}
        onCancel={() => setPackageModalVisible(false)}
        footer={null}
      >
        <Form
          form={packageForm}
          layout="vertical"
          onFinish={handleCreatePackage}
        >
          <Form.Item
            name="type"
            label="包类型"
            rules={[{ required: true, message: '请选择包类型' }]}
          >
            <Select>
              <Option value="incremental">增量更新包 (只包含变更文件)</Option>
              <Option value="full">完整部署包 (包含所有文件)</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="build"
            label="包含build文件夹"
            valuePropName="checked"
            tooltip="勾选后将包含frontend/build文件夹到更新包中（如果build文件夹存在）"
          >
            <Switch />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea
              rows={3}
              placeholder="请输入本次更新的描述..."
            />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                创建更新包
              </Button>
              <Button onClick={() => setPackageModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加/编辑服务器模态框 */}
      <Modal
        title={editingServer ? "编辑服务器" : "添加服务器"}
        open={serverModalVisible}
        onCancel={() => {
          setServerModalVisible(false);
          setEditingServer(null);
          serverForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={serverForm}
          layout="vertical"
          onFinish={handleAddServer}
        >
          <Form.Item
            name="name"
            label="服务器名称"
            rules={[{ required: true, message: '请输入服务器名称' }]}
          >
            <Input placeholder="例如: vps1" />
          </Form.Item>
          
          <Form.Item
            name="host"
            label="主机地址"
            rules={[{ required: true, message: '请输入主机地址' }]}
          >
            <Input placeholder="例如: 192.168.1.100" />
          </Form.Item>
          
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="例如: root" />
          </Form.Item>
          
          <Form.Item
            name="deployPath"
            label="部署路径"
            rules={[{ required: true, message: '请输入部署路径' }]}
          >
            <Input placeholder="例如: /opt/gamepanel" />
          </Form.Item>
          
          <Form.Item
            name="port"
            label="端口"
            initialValue={22}
          >
            <Input type="number" />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="密码 (可选)"
          >
            <Input.Password placeholder="留空则使用SSH密钥" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingServer ? "更新服务器" : "添加服务器"}
              </Button>
              <Button onClick={() => {
                setServerModalVisible(false);
                setEditingServer(null);
                serverForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 生成部署脚本模态框 */}
      <Modal
        title="生成部署脚本"
        open={deployModalVisible}
        onCancel={() => setDeployModalVisible(false)}
        footer={null}
      >
        <Form
          form={deployForm}
          layout="vertical"
          onFinish={handleGenerateDeployScript}
        >
          <Form.Item
            name="packagePath"
            label="更新包路径"
            rules={[{ required: true, message: '请选择更新包' }]}
          >
            <Input disabled />
          </Form.Item>
          
          <Form.Item
            name="serverName"
            label="目标服务器"
            rules={[{ required: true, message: '请选择服务器' }]}
          >
            <Select placeholder="请选择服务器">
              {Object.keys(servers).map(name => (
                <Option key={name} value={name}>
                  {name} {name === defaultServer && '(默认)'}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="type"
            label="部署类型"
            rules={[{ required: true, message: '请选择部署类型' }]}
          >
            <Select>
              <Option value="incremental">增量更新</Option>
              <Option value="full">完整部署</Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                生成部署脚本
              </Button>
              <Button onClick={() => setDeployModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 自动部署模态框 */}
      <Modal
        title="自动部署"
        open={autoDeployModalVisible}
        onCancel={() => {
          setAutoDeployModalVisible(false);
          setSelectedPackage(null);
        }}
        footer={null}
      >
        {selectedPackage && (
          <div>
            <Alert
              message="自动部署说明"
              description={
                <div>
                  <p>将自动执行以下操作：</p>
                  <ul>
                    <li>📤 上传部署包到服务器</li>
                    <li>📦 创建备份（完整部署时）</li>
                    <li>📂 解压部署包到目标目录</li>
                    <li>🔧 执行部署后脚本（如果存在）</li>
                  </ul>
                  <p><strong>部署包：</strong>{selectedPackage.name}</p>
                  <p><strong>类型：</strong>{selectedPackage.type === 'incremental' ? '增量更新' : '完整部署'}</p>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form
              layout="vertical"
              onFinish={handleExecuteAutoDeploy}
            >
              <Form.Item
                name="serverId"
                label="目标服务器"
                rules={[{ required: true, message: '请选择服务器' }]}
              >
                <Select placeholder="请选择服务器">
                  {Object.values(servers).map((server: any) => (
                    <Option key={server.id} value={server.id}>
                      {server.name} ({server.host}:{server.port})
                      {server.is_default && ' - 默认'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={deploying}
                    icon={<CloudUploadOutlined />}
                  >
                    {deploying ? '部署中...' : '开始自动部署'}
                  </Button>
                  <Button onClick={() => {
                    setAutoDeployModalVisible(false);
                    setSelectedPackage(null);
                  }}>
                    取消
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* 版本历史模态框 */}
      <Modal
        title="版本历史"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={800}
      >
        <Timeline>
          {versionHistory.map((version, index) => (
            <Timeline.Item
              key={version.version}
              color={index === 0 ? 'green' : 'blue'}
            >
              <div>
                <Title level={4}>
                  v{version.version}
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {new Date(version.timestamp).toLocaleDateString()}
                  </Tag>
                </Title>
                <Paragraph>{version.description}</Paragraph>
                <Text type="secondary">
                  变更文件: {version.changedFiles} 个 | 
                  总文件: {version.totalFiles} 个
                </Text>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Modal>

      {/* 部署日志模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>部署日志</span>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => loadDeployLogs(true)}
              loading={refreshingLogs}
            >
              刷新
            </Button>
          </div>
        }
        open={deployLogsModalVisible}
        onCancel={() => setDeployLogsModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Table
          dataSource={deployLogs}
          columns={[
            {
              title: '部署包',
              dataIndex: 'package_name',
              key: 'package_name',
              render: (text: string, record: any) => (
                <Space>
                  <FileZipOutlined />
                  <Text strong>{text}</Text>
                  <Tag color={record.deploy_type === 'incremental' ? 'blue' : 'green'}>
                    {record.deploy_type === 'incremental' ? '增量' : '完整'}
                  </Tag>
                </Space>
              )
            },
            {
              title: '服务器',
              dataIndex: 'server_name',
              key: 'server_name',
              render: (text: string, record: any) => (
                <Space>
                  <CloudUploadOutlined />
                  <Text>{text}</Text>
                  <Text type="secondary">({record.host}:{record.port})</Text>
                </Space>
              )
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              render: (status: string, record: any) => {
                const statusConfig = {
                  pending: { color: 'default', text: '等待中' },
                  uploading: { color: 'processing', text: '上传中' },
                  extracting: { color: 'processing', text: '解压中' },
                  deploying: { color: 'processing', text: '部署中' },
                  completed: { color: 'success', text: '完成' },
                  failed: { color: 'error', text: '失败' }
                };
                const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
                return (
                  <Space>
                    <Tag color={config.color}>{config.text}</Tag>
                    {status === 'uploading' || status === 'extracting' || status === 'deploying' ? (
                      <Progress percent={record.progress} size="small" style={{ width: 60 }} />
                    ) : null}
                  </Space>
                );
              }
            },
            {
              title: '开始时间',
              dataIndex: 'started_at',
              key: 'started_at',
              render: (text: string) => new Date(text).toLocaleString()
            },
            {
              title: '完成时间',
              dataIndex: 'completed_at',
              key: 'completed_at',
              render: (text: string) => text ? new Date(text).toLocaleString() : '-'
            },
            {
              title: '操作',
              key: 'actions',
              render: (text: any, record: any) => (
                <Space>
                  <Button
                    size="small"
                    icon={<InfoCircleOutlined />}
                    onClick={() => {
                      Modal.info({
                        title: '部署详情',
                        width: 600,
                        content: (
                          <div>
                            <Descriptions column={1} size="small">
                              <Descriptions.Item label="部署包">{record.package_name}</Descriptions.Item>
                              <Descriptions.Item label="服务器">{record.server_name} ({record.host}:{record.port})</Descriptions.Item>
                              <Descriptions.Item label="部署类型">{record.deploy_type === 'incremental' ? '增量更新' : '完整部署'}</Descriptions.Item>
                              <Descriptions.Item label="状态">{record.status}</Descriptions.Item>
                              <Descriptions.Item label="进度">{record.progress}%</Descriptions.Item>
                              <Descriptions.Item label="开始时间">{new Date(record.started_at).toLocaleString()}</Descriptions.Item>
                              <Descriptions.Item label="完成时间">{record.completed_at ? new Date(record.completed_at).toLocaleString() : '-'}</Descriptions.Item>
                              {record.log_message && (
                                <Descriptions.Item label="日志消息">
                                  <Text code>{record.log_message}</Text>
                                </Descriptions.Item>
                              )}
                              {record.error_message && (
                                <Descriptions.Item label="错误信息">
                                  <Text type="danger">{record.error_message}</Text>
                                </Descriptions.Item>
                              )}
                            </Descriptions>
                          </div>
                        )
                      });
                    }}
                  >
                    详情
                  </Button>
                </Space>
              )
            }
          ]}
          pagination={false}
          size="small"
          rowKey="id"
        />
      </Modal>

      {/* 变更文件列表模态框 */}
      <Modal
        title="变更文件列表"
        open={changedFilesModalVisible}
        onCancel={() => setChangedFilesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setChangedFilesModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {changedFiles.length === 0 ? (
            <Alert
              message="没有检测到文件变更"
              description="当前没有文件发生变更"
              type="info"
              showIcon
            />
          ) : (
            <Table
              dataSource={changedFiles}
              columns={[
                {
                  title: '文件路径',
                  dataIndex: 'path',
                  key: 'path',
                  render: (text: string) => (
                    <Text code style={{ fontSize: '12px' }}>
                      {text}
                    </Text>
                  )
                },
                {
                  title: '大小',
                  dataIndex: 'size',
                  key: 'size',
                  width: 100,
                  render: (size: number) => `${(size / 1024).toFixed(2)} KB`
                },
                {
                  title: '修改时间',
                  dataIndex: 'modified',
                  key: 'modified',
                  width: 180,
                  render: (time: string) => new Date(time).toLocaleString()
                }
              ]}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个文件`
              }}
              size="small"
              rowKey="path"
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VersionManagement;
